// ZOC Medical — Programs data
// Split out of zocData.js for maintainability.

export const PROGRAMS = [
  {
    slug: "awv",
    code: "AWV",
    name: "Annual Wellness Visits",
    tagline: "The yearly visit that sets the whole care plan in motion.",
    color: "#2E6FB0",
    category: "Preventive",
    eligibility: "Medicare patients (after 12 months of Part B)",
    billingModel: "Per-visit HCPCS",
    codes: ["G0438", "G0439"],
    monthlyRate: "$118 – $175 / visit",
    timeRequired: "One structured annual visit",
    uniqueValue: "The anchor visit that surfaces every care opportunity.",
    description:
      "The Annual Wellness Visit builds a personalized prevention plan from a health-risk assessment — the natural on-ramp for enrolling patients into the ongoing programs they qualify for.",
    documentation: [
      "Health risk assessment (HRA) completed",
      "Personalized prevention plan on file",
      "Screening schedule & referrals documented",
      "Cognitive & functional assessment",
      "Once per 12-month period",
    ],
    compatibleWith: ["ACP", "APCM"],
    exclusiveWith: [],
  },
  {
    slug: "apcm",
    code: "APCM",
    name: "Advanced Primary Care Management",
    tagline: "The foundation of proactive primary care.",
    color: "#1E5AA8",
    category: "Primary care",
    eligibility: "All Medicare Part B patients",
    billingModel: "Activity-based HCPCS G-codes",
    codes: ["G0556", "G0557", "G0558"],
    monthlyRate: "$16 – $117 / patient",
    rateLow: 16,
    rateHigh: 117,
    timeRequired: "None — no time tracking",
    uniqueValue: "Widest patient eligibility with no time tracking burden.",
    description:
      "APCM replaces time-based billing with an activity-based model, letting every Medicare Part B patient receive structured, continuous primary care management without a stopwatch running in the background.",
    documentation: [
      "Patient consent (captured once)",
      "13 service elements documented",
      "Electronic care plan on file",
      "Risk stratification completed",
      "Quality reporting recorded",
    ],
    compatibleWith: ["RPM", "RTM"],
    exclusiveWith: ["CCM"],
  },
  {
    slug: "rpm",
    code: "RPM",
    name: "Remote Patient Monitoring",
    tagline: "Real-time physiological data, delivered to the care team.",
    color: "#128C8C",
    category: "Monitoring",
    eligibility: "Medicare — acute or chronic condition",
    billingModel: "Device + time-based CPT",
    codes: ["99453", "99454", "99457", "99458"],
    monthlyRate: "$52 – $144 / patient",
    rateLow: 52,
    rateHigh: 144,
    timeRequired: "≥ 20 min management time / month",
    uniqueValue: "Continuous physiologic signals with real-time vital sign alerts.",
    description:
      "RPM streams connected-device readings — blood pressure, glucose, weight, SpO₂ — into the clinical workflow so the team can intervene before a crisis becomes a hospitalization.",
    documentation: [
      "Separate patient consent",
      "≥ 16 days device data per month",
      "≥ 20 min management time logged",
      "≥ 1 real-time communication",
      "Monthly transmission logs",
    ],
    compatibleWith: ["APCM", "CCM"],
    exclusiveWith: ["RTM"],
  },
  {
    slug: "ccm",
    code: "CCM",
    name: "Chronic Care Management",
    tagline: "The strongest care-coordination framework in Medicare.",
    color: "#0E7C7C",
    category: "Chronic care",
    eligibility: "Medicare — 2+ chronic conditions",
    billingModel: "Time-based CPT",
    codes: ["99490", "99439", "99487", "99489", "99491"],
    monthlyRate: "$66 – $144 / patient",
    rateLow: 66,
    rateHigh: 144,
    timeRequired: "≥ 20 min (standard) · ≥ 60 min (complex)",
    uniqueValue: "Broadest code set and the deepest coordination framework.",
    description:
      "CCM wraps patients with two or more chronic conditions in a comprehensive, 24/7-accessible care plan — the backbone for high-complexity populations that drive the majority of Medicare spend.",
    documentation: [
      "Consent with cost disclosure",
      "≥ 20 or ≥ 60 min time log",
      "Comprehensive care plan",
      "Care coordination records",
      "24/7 access documentation",
    ],
    compatibleWith: ["RPM", "RTM"],
    exclusiveWith: ["APCM"],
  },
  {
    slug: "rtm",
    code: "RTM",
    name: "Remote Therapeutic Monitoring",
    tagline: "The only program where PTs, OTs & SLPs bill directly.",
    color: "#4E9E33",
    category: "Monitoring",
    eligibility: "Medicare — MSK, respiratory, or CBT need",
    billingModel: "Device / software + time-based CPT",
    codes: ["98975", "98977", "98976", "98978", "98980", "98981"],
    monthlyRate: "$77 – $135 / patient",
    rateLow: 77,
    rateHigh: 135,
    timeRequired: "≥ 20 min / month",
    uniqueValue: "Extends monitoring revenue to therapy disciplines.",
    description:
      "RTM captures non-physiologic therapy data — musculoskeletal, respiratory and cognitive-behavioral — and is uniquely billable by physical, occupational and speech-language therapists.",
    documentation: [
      "Written consent (annual)",
      "≥ 16 days data or ≥ 2 days (partial)",
      "≥ 20 min treatment management",
      "≥ 1 real-time communication",
      "Therapy plan of care (PT/OT/SLP)",
    ],
    compatibleWith: ["APCM", "CCM"],
    exclusiveWith: ["RPM"],
  },
  {
    slug: "acp",
    code: "ACP",
    name: "Advance Care Planning",
    tagline: "Honoring patient wishes — documented and reimbursed.",
    color: "#6FBE44",
    category: "Preventive",
    eligibility: "Medicare patients — any care setting",
    billingModel: "Time-based CPT",
    codes: ["99497", "99498"],
    monthlyRate: "$80 – $235 / session",
    timeRequired: "≥ 16 min (first 30-min unit)",
    uniqueValue: "Meaningful goals-of-care conversations, on the record.",
    description:
      "Advance Care Planning gives clinicians paid, unhurried time to discuss goals of care and advance directives — often bundled with the Annual Wellness Visit at no cost to the patient.",
    documentation: [
      "Voluntary discussion documented",
      "Time spent recorded (≥ 16 min)",
      "Advance directive / decisions noted",
      "Patient or surrogate participation",
      "No cost-share when billed with AWV",
    ],
    compatibleWith: ["AWV", "APCM", "CCM"],
    exclusiveWith: [],
  },
  {
    slug: "cocm",
    code: "CoCM",
    name: "Collaborative Care Management",
    tagline: "Integrated behavioral health, measured and managed.",
    color: "#16A394",
    category: "Behavioral health",
    eligibility: "Medicare patients with a behavioral health condition",
    billingModel: "Time-based CPT (monthly)",
    codes: ["99492", "99493", "99494", "G2214"],
    monthlyRate: "$65 – $160 / patient",
    timeRequired: "≥ 36–70 min / month (care manager)",
    uniqueValue: "A psychiatric care team wrapped around primary care.",
    description:
      "The Collaborative Care Model integrates a behavioral-health care manager and psychiatric consultant into the practice, using a patient registry and measurement-based treatment to close the mental-health gap.",
    documentation: [
      "Patient consent to CoCM",
      "Validated symptom tracking (e.g. PHQ-9)",
      "Registry-based caseload review",
      "Psychiatric consultant input",
      "Monthly care-manager time log",
    ],
    compatibleWith: ["APCM", "CCM"],
    exclusiveWith: [],
  },
  {
    slug: "tcm",
    code: "TCM",
    name: "Transition Care Management",
    tagline: "The safety net when a patient leaves the hospital.",
    color: "#D97706",
    category: "Care transitions",
    eligibility: "Medicare — post-discharge from inpatient, observation or SNF",
    billingModel: "One-time post-discharge CPT",
    codes: ["99495", "99496"],
    monthlyRate: "$204 – $281 / discharge",
    rateLow: 204,
    rateHigh: 281,
    timeRequired: "Interactive contact ≤ 2 business days · face-to-face ≤ 7 or 14 days",
    uniqueValue: "Highest-value 30-day service — one billable window per discharge.",
    description:
      "TCM covers the fragile 30 days after a hospital, observation or SNF stay. With ADT alerts from the HIE, ZOC surfaces the discharge, prompts the 2-day interactive contact, tracks medication reconciliation and readies the qualifying face-to-face visit — cutting readmissions and capturing 99495/99496 revenue.",
    documentation: [
      "Interactive contact ≤ 2 business days post-discharge",
      "Medication reconciliation before/at face-to-face",
      "Face-to-face visit ≤ 7 days (high) or ≤ 14 days (moderate)",
      "Medical decision-making complexity documented",
      "30-day service period notes with dates",
    ],
    compatibleWith: ["CCM", "APCM", "RPM"],
    exclusiveWith: [],
  },
];

// Programs whose concurrent-billing rules are covered by the compatibility matrix
export const CORE_MATRIX_CODES = ["APCM", "RPM", "CCM", "RTM"];

// Concurrent billing matrix. true = valid, false = never bill together, null = same (self)
export const COMPAT_MATRIX = {
  APCM: { APCM: null, RPM: true, CCM: false, RTM: true },
  RPM: { APCM: true, RPM: null, CCM: true, RTM: false },
  CCM: { APCM: false, RPM: true, CCM: null, RTM: true },
  RTM: { APCM: true, RPM: false, CCM: true, RTM: null },
};

export const AUDIT_RED_FLAGS = [
  "Billing APCM and CCM for the same patient in the same month",
  "Billing RPM and RTM concurrently for the same patient",
  "Shared or copied documentation across concurrent programs",
  "Missing or undated patient consent",
  "Time logs that don't meet the program minimum",
];

export const getProgram = (slug) => PROGRAMS.find((p) => p.slug === slug);
